/**
 * Quick "Share my vault" button — opens a tiny modal where the candidate
 * picks expiry + which sections to include, then creates a /vault/access-links
 * token and copies the share URL to clipboard.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Share2, Copy, Check, X } from 'lucide-react';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TOGGLES = {
  include_employment: true,
  include_documents: false,
  include_pii: false,
  include_ai_profile: true,
  include_compliance_readiness: true,
};

export default function ShareVaultButton() {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(24);
  const [toggles, setToggles] = useState(DEFAULT_TOGGLES);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/vault/access-links`, {
        expiry_hours: hours,
        ...toggles,
      });
      setLink(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create share link.');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!link?.share_url) return;
    try {
      await navigator.clipboard.writeText(link.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard API may be unavailable (insecure context, permissions). Surface a
      // friendly hint rather than silently doing nothing.
      logger.warn('clipboard copy failed', e?.message);
      setError('Could not copy automatically. Please select and copy the link manually.');
    }
  };

  const reset = () => {
    setOpen(false);
    setLink(null);
    setError('');
    setHours(24);
    setToggles(DEFAULT_TOGGLES);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--vh-accent)] hover:bg-[var(--vh-accent-hover)] text-white text-sm font-medium shadow-sm"
        data-testid="vault-share-button"
      >
        <Share2 className="w-4 h-4" />
        Share my vault
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-2xl border border-[var(--vh-border)] w-full max-w-md p-6" data-testid="vault-share-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl text-[var(--vh-text-primary)]">Share your vault</h3>
                <p className="text-xs text-[var(--vh-text-secondary)] mt-1">A one-time, time-limited link. Revoke any time.</p>
              </div>
              <button type="button" onClick={reset} aria-label="Close" className="p-1 rounded hover:bg-[var(--vh-bg-muted)]" data-testid="vault-share-close">
                <X className="w-5 h-5 text-[var(--vh-text-muted)]" />
              </button>
            </div>

            {!link ? (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5 text-[var(--vh-text-secondary)]">Expires after</label>
                    <select
                      value={hours}
                      onChange={(e) => setHours(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)]"
                      data-testid="vault-share-expiry"
                    >
                      <option value={4}>4 hours</option>
                      <option value={24}>24 hours</option>
                      <option value={72}>3 days</option>
                      <option value={168}>7 days</option>
                    </select>
                  </div>

                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-[var(--vh-text-secondary)] mb-1">Include in this share</legend>
                    {[
                      ['include_employment', 'Employment history'],
                      ['include_ai_profile', 'Approved AI profile'],
                      ['include_compliance_readiness', 'Compliance pack readiness (% only)'],
                      ['include_documents', 'Vault documents'],
                      ['include_pii', 'Personal identifiable information (PII)'],
                    ].map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 text-sm text-[var(--vh-text-primary)]">
                        <input
                          type="checkbox"
                          checked={!!toggles[key]}
                          onChange={(e) => setToggles({ ...toggles, [key]: e.target.checked })}
                          className="h-4 w-4 rounded border-[var(--vh-border)] text-[var(--vh-brand-primary)]"
                          data-testid={`vault-share-${key}`}
                        />
                        {label}
                      </label>
                    ))}
                  </fieldset>

                  {error && <p className="text-sm text-red-700" data-testid="vault-share-error">{error}</p>}
                </div>

                <div className="mt-6 flex gap-2 justify-end">
                  <button type="button" onClick={reset} className="px-4 py-2 rounded-full border border-[var(--vh-border)] text-sm">Cancel</button>
                  <button
                    type="button"
                    onClick={create}
                    disabled={loading}
                    className="px-5 py-2 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-sm font-medium disabled:opacity-60"
                    data-testid="vault-share-create"
                  >
                    {loading ? 'Creating…' : 'Create link'}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4" data-testid="vault-share-result">
                <p className="text-sm text-[var(--vh-text-primary)]">Your link is ready. It expires {new Date(link.expiry_date).toLocaleString()}.</p>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={link.share_url}
                    className="flex-1 min-w-0 px-3 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-muted)] text-xs text-[var(--vh-text-primary)]"
                    data-testid="vault-share-url"
                  />
                  <button
                    type="button"
                    onClick={copy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-xs"
                    data-testid="vault-share-copy"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-[var(--vh-text-muted)]">View this and other live links from the Audit Trail tab. You can revoke any link at any time.</p>
                <div className="flex justify-end">
                  <button type="button" onClick={reset} className="px-4 py-2 rounded-full bg-[var(--vh-bg-muted)] hover:bg-[var(--vh-border)] text-sm">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
